import { ReactElement, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useAuth } from '../../contextProviders/AuthProvider';
import { useSuperUserMutators } from '../../dataMutators/useSuperUserMutators';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import { isValidDocumentId } from '../../utils/utils';

import GodRefreshCoursesMenuItem from './GodRefreshCoursesMenuItem';

interface GratiGodActionsProps {
  pageGodActions?: ReactElement | null;
}

const GOD_NAV_ITEMS = [
  { label: 'Competitions', path: '/competition-library' },
  { label: 'USGA', path: '/usga-test' },
  { label: 'Edit Firestore', path: '/firestore-utility' },
  { label: 'Import events', path: '/event-uploader' },
  { label: `Update ${HANDICAP_LABELS.INDEX} daily`, path: '/daily-ghin-uploader' },
  { label: 'Support', path: '/user-support' },
  { label: 'Email templates', path: '/email-templates' },
  { label: 'Vanity URLs', path: '/org-vanity-url-requests' },
  { label: 'Send guidance', path: '/guidance-action-creator' },
  // The emulator delivers no mail, so this is where to read what would have been sent.
  ...(import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' ? [{ label: 'Dev outbox', path: '/dev-outbox' }] : []),
];

export default function GratiGodActions({ pageGodActions }: GratiGodActionsProps) {
  const navigate = useNavigate();
  const { isGod, isAuthGod, impersonating } = useAuth();
  const { toggleGodStatus, cancelImpersonation, startImpersonation } = useSuperUserMutators();
  const [isToggling, setIsToggling] = useState(false);
  const [impersonateId, setImpersonateId] = useState('');
  const [impersonateError, setImpersonateError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  if (!isGod && !isAuthGod) {
    return null;
  }

  const handleToggleGod = async (enable: boolean) => {
    setIsToggling(true);
    try {
      await toggleGodStatus(enable);
    } catch (error) {
      console.error('Error toggling god status:', error);
    } finally {
      setIsToggling(false);
    }
  };

  const handleImpersonateSubmit = () => {
    const id = impersonateId.trim();
    if (!id) return;
    if (!isValidDocumentId(id)) {
      setImpersonateError('Not a profile ID');
      return;
    }
    setImpersonateError(null);
    startImpersonation(id);
    setImpersonateId('');
  };

  const handleNavAndClose = (path: string) => {
    setMenuAnchor(null);
    navigate(path);
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
      {isAuthGod && (
        <ToggleButtonGroup
          value={isGod ? 'god' : 'mortal'}
          exclusive
          onChange={(_, newValue) => {
            if (newValue !== null) {
              handleToggleGod(newValue === 'god');
            }
          }}
          disabled={isToggling}
          size="small"
        >
          <ToggleButton value="god">God</ToggleButton>
          <ToggleButton value="mortal">Mortal</ToggleButton>
        </ToggleButtonGroup>
      )}
      {isAuthGod && (
        impersonating ? (
          <Chip
            label={impersonating}
            size="small"
            onDelete={() => cancelImpersonation()}
            sx={{ fontFamily: 'monospace' }}
          />
        ) : (
          <TextField
            size="small"
            placeholder="Profile ID"
            value={impersonateId}
            onChange={(e) => { setImpersonateId(e.target.value); setImpersonateError(null); }}
            error={!!impersonateError}
            helperText={impersonateError ?? undefined}
            onKeyDown={(e) => { if (e.key === 'Enter') handleImpersonateSubmit(); }}
            sx={{ width: '12em' }}
          />
        )
      )}
      {isGod && (
        <>
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label="More god actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            {pageGodActions && (
              <MenuItem onClick={() => setMenuAnchor(null)} disableRipple sx={{ pointerEvents: 'none' }}>
                {pageGodActions}
              </MenuItem>
            )}
            {GOD_NAV_ITEMS.map(({ label, path }) => (
              <MenuItem key={path} onClick={() => handleNavAndClose(path)}>
                {label}
              </MenuItem>
            ))}
            <GodRefreshCoursesMenuItem />
          </Menu>
        </>
      )}
    </Stack>
  );
}
